import cx from 'classnames'
import { useState } from 'react'
import dayjs from 'dayjs'

import Styles from './NoWebsite.module.scss'

import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCreateBrandProfile } from 'hooks/queries/brandProfiles/useCreateBrandProfile'
import { useCreateMediaObjects } from 'hooks/queries/mediaObjects/useCreateMediaObjects'

import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { BrandProfile, MediaObject } from 'types'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Input } from 'almanac-ui/TextInputField/Input'
import { ArrowLeft, ArrowRight, File02, Image, Plus, Trash2, Upload5 } from 'blaze-ui/icons/20'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { Image03 } from 'blaze-ui/icons/24'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ACCEPTED_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'
import { useUpdateIterableProfile } from 'hooks/queries/useUpdateIterableProfile'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'
import { Chip } from 'components/Chip/Chip'

const Row = ({ type, url, onDelete }: { type: 'visual' | 'document'; url: string; onDelete: () => void }) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const fileName = url.split('/').pop()

  if (!fileName) return null

  return (
    <div className={Styles.row}>
      <div className={Styles.iconAndUrl}>
        <div className={Styles.icon}>
          {type === 'visual' && <Image03 size={isOnboardingRedesign ? 20 : undefined} />}
          {type === 'document' && <File02 size={isOnboardingRedesign ? 20 : 24} />}
        </div>
        <Text variant={isOnboardingRedesign ? 'secondary' : 'smallList'} lineClamp={1}>
          {fileName}
        </Text>
      </div>
      <IconButton variant="editor" onPress={onDelete} icon={Trash2} size={isOnboardingRedesign ? 'md' : undefined} />
    </div>
  )
}

interface Props {
  onBack: () => void
  onNext: (brandProfile: BrandProfile) => void
  url?: string
  brandKitId?: number
  selectedLanguage?: string
}

export const NoWebsite = ({ onBack, onNext, brandKitId, selectedLanguage, url }: Props) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const { isMobile } = useResponsiveDesign()
  const [brandName, setBrandName] = useState('')
  const [productsAndServices, setProductsAndServices] = useState('')
  const [customers, setCustomers] = useState('')
  const [customerLocation, setCustomerLocation] = useState('')
  const [additionalText, setAdditionalText] = useState('')
  const [visuals, setVisuals] = useState<MediaObject[]>([])
  const [documents, setDocuments] = useState<MediaObject[]>([])
  const { mutateAsync: createMediaObjects, isLoading: isCreatingMediaObjects } = useCreateMediaObjects()
  const { mutateAsync: updateIterableProfile } = useUpdateIterableProfile()
  const { mutate, isLoading: isLoadingBrandProfile } = useCreateBrandProfile()
  let imageLoadingToast
  let documentLoadingToast

  const handleImagesSelect = async (files: FileList | null) => {
    if (!files) return
    imageLoadingToast = showToastMessage('Uploading...', 'loading', undefined, 0)
    const fileArray = Array.from(files)
    const mediaObjects = await createMediaObjects(fileArray.map((file) => ({ file, filename: file.name })))
    setVisuals([...visuals, ...mediaObjects])
    imageLoadingToast?.()
  }

  const handleDocumentsSelect = async (files: FileList | null) => {
    if (!files) return
    documentLoadingToast = showToastMessage('Uploading...', 'loading', undefined, 0)
    const fileArray = Array.from(files)
    const mediaObjects = await createMediaObjects(fileArray.map((file) => ({ file, filename: file.name })))
    setDocuments([...documents, ...mediaObjects])
    documentLoadingToast?.()
  }

  const handleSubmit = async () => {
    if (!brandName || !productsAndServices || !customers || !customerLocation) {
      showToastMessage('Please fill in all required fields', 'error')
      return
    }

    if (visuals.length === 0) {
      showToastMessage('Please upload at least one visual', 'error')
      return
    }

    mutate(
      {
        url: visuals[0].source_url,
        screenshot_media_object_ids: visuals?.map((visual) => visual.id) || [],
        document_media_object_ids: documents?.map((document) => document.id) || [],
        brand_info: {
          brand_name: brandName,
          products_and_services: productsAndServices,
          customers,
          customer_location: customerLocation,
          additional_text: additionalText,
        },
        language: selectedLanguage,
        timezone: dayjs.tz.guess(),
      },
      {
        onSuccess: (data) => {
          updateIterableProfile({ onboarding_device: isMobile ? 'mobile' : 'desktop' })
          onNext(data.brand_profile)
        },
        onError: (error) => {
          showToastMessage(error.message, 'error')
        },
      },
    )
  }

  const handleDeleteDocument = (documentId: number) => {
    setDocuments((prevDocuments) => prevDocuments.filter((doc) => doc.id !== documentId))
  }

  const handleDeleteVisual = (visualId: number) => {
    setVisuals((prevVisuals) => prevVisuals.filter((visual) => visual.id !== visualId))
  }

  return (
    <MultiStepStepLayout
      title="Let's create your Brand Profile"
      description="We'll need some information to draft a more detailed Brand Profile."
    >
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <ResponsiveDesign.Desktop>
                <Button
                  id="no-website-back"
                  variant="tertiary"
                  size={isOnboardingRedesign ? 'lg' : 'xl'}
                  onPress={onBack}
                  frontIcon={isOnboardingRedesign ? undefined : ArrowLeft}
                  aria-label="Back"
                  isDisabled={isLoadingBrandProfile}
                >
                  Back
                </Button>
              </ResponsiveDesign.Desktop>
              <Button
                id="no-website-next"
                size={isOnboardingRedesign ? 'lg' : 'xl'}
                onPress={handleSubmit}
                endIcon={isOnboardingRedesign ? undefined : ArrowRight}
                aria-label="Continue"
                isDisabled={isLoadingBrandProfile}
                fullWidth={isMobile}
              >
                {isLoadingBrandProfile ? 'Saving...' : 'Continue'}
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <div className={Styles.container}>
          <div className={Styles.section}>
            <div className={Styles.inputGroup}>
              <label htmlFor="brand-name">
                <Heading level={isMobile ? 5 : 4}>
                  What is your brand's name? <span className={Styles.redAsterisk}>*</span>
                </Heading>
              </label>
              <Input
                className={cx(Styles.input, { [Styles.darkBackground]: isMobile && brandName.length === 0 })}
                id="brand-name"
                name="brand-name"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Enter brand name"
                size="lg"
                autoFocus
                autoCorrect="off"
              />
            </div>
            <div className={Styles.inputGroup}>
              <label htmlFor="products-and-services">
                <Heading level={isMobile ? 5 : 4}>
                  What products or services do you offer? <span className={Styles.redAsterisk}>*</span>
                </Heading>
              </label>
              <Input
                className={cx(Styles.input, { [Styles.darkBackground]: isMobile && productsAndServices.length === 0 })}
                id="products-and-services"
                name="products-and-services"
                value={productsAndServices}
                onChange={(e) => setProductsAndServices(e.target.value)}
                placeholder="Financial advice, planning, and wealth management"
                size="lg"
                autoFocus={false}
                autoCorrect="off"
              />
            </div>
            <div className={Styles.inputGroup}>
              <label htmlFor="customers">
                <Heading level={isMobile ? 5 : 4}>
                  Describe your typical customers. Be specific. <span className={Styles.redAsterisk}>*</span>
                </Heading>
              </label>
              <Input
                className={cx(Styles.input, { [Styles.darkBackground]: isMobile && customers.length === 0 })}
                id="customers"
                name="customers"
                value={customers}
                onChange={(e) => setCustomers(e.target.value)}
                placeholder="Middle-income families saving for college and retirement"
                size="lg"
                autoFocus={false}
                autoCorrect="off"
              />
            </div>
            <div className={Styles.inputGroup}>
              <label htmlFor="customer-location">
                <Heading level={isMobile ? 5 : 4}>
                  Where are your customers located? <span className={Styles.redAsterisk}>*</span>
                </Heading>
              </label>
              <Input
                className={cx(Styles.input, { [Styles.darkBackground]: isMobile && customerLocation.length === 0 })}
                id="customer-location"
                name="customer-location"
                value={customerLocation}
                onChange={(e) => setCustomerLocation(e.target.value)}
                placeholder="Topeka, Kansas, USA or English speaking countries"
                size="lg"
                autoFocus={false}
                autoCorrect="off"
              />
            </div>
          </div>
          <div className={Styles.section}>
            <div className={Styles.brandVisualsDescription}>
              {!isOnboardingRedesign && (
                <ResponsiveDesign.Desktop>
                  <Image size={36} />
                </ResponsiveDesign.Desktop>
              )}
              <div className={Styles.textContainer}>
                {isOnboardingRedesign ? (
                  <Heading level={4}>
                    Upload brand visuals so we can create your visual identity{' '}
                    <span className={Styles.redAsterisk}>*</span>
                  </Heading>
                ) : (
                  <Heading level={isMobile ? 5 : 4}>
                    Required: Upload brand visuals so we can create your visual identity{' '}
                  </Heading>
                )}
                <Text variant="secondary" color={isOnboardingRedesign ? 'var(--dark-40)' : 'var(--dark-80)'}>
                  Provide a few screenshots or images of posters, brochures, posts, or newsletters that showcase your
                  brand's colors, fonts, and style.
                </Text>
              </div>
            </div>
            {visuals.length === 0 && (
              <>
                {isOnboardingRedesign ? (
                  <div className={Styles.brandVisualsEmptyStateRedesigned}>
                    <div className={Styles.top}>
                      <img
                        src="https://res.cloudinary.com/almanac/image/upload/v1765645882/blaze_assets/virtual_marketer/onboarding/brand_visuals_qsrbuv.png"
                        alt="Brand visuals"
                        className={Styles.brandVisualsEmptyStateImage}
                      />
                      <Text variant="secondary" color="var(--dark-40)">
                        Examples include: <br /> social media posts, product photos, email newsletters
                      </Text>
                    </div>
                    <div className={Styles.bottom}>
                      <FileTrigger
                        accept={ACCEPTED_IMAGE_MIME_TYPES_NO_SVG}
                        allowsMultiple={true}
                        onSelect={handleImagesSelect}
                      >
                        <Button
                          variant="secondary"
                          size="xl"
                          frontIcon={Upload5}
                          isDisabled={isCreatingMediaObjects}
                          fullWidth={isMobile}
                        >
                          Upload Images
                        </Button>
                      </FileTrigger>
                      <Text variant="metadata" color="var(--dark-40)">
                        Supported formats: PNG and JPG
                      </Text>
                    </div>
                  </div>
                ) : (
                  <div className={Styles.brandVisualsEmptyState}>
                    <div className={Styles.examples}>
                      <Text variant="label">Examples:</Text>
                      <div className={Styles.exampleImagesContainer}>
                        <div className={Styles.exampleImageContainer}>
                          <img
                            src="https://res.cloudinary.com/almanac/image/upload/v1758554497/onboarding_assets/no_website_step/brand_visual_example_1_sykm1k.png"
                            alt="Social post"
                          />
                          <Text variant="metadata" color="var(--dark-40)">
                            Social post
                          </Text>
                        </div>
                        <div className={Styles.exampleImageContainer}>
                          <img
                            src="https://res.cloudinary.com/almanac/image/upload/v1758554769/onboarding_assets/no_website_step/brand_visual_example_2_javzcp.png"
                            alt="Product"
                          />
                          <Text variant="metadata" color="var(--dark-40)">
                            Product photo
                          </Text>
                        </div>
                        <div className={Styles.exampleImageContainer}>
                          <img
                            src="https://res.cloudinary.com/almanac/image/upload/v1758554832/onboarding_assets/no_website_step/brand_visual_example_3_y34hn5.png"
                            alt="Email newsletter"
                          />
                          <Text variant="metadata" color="var(--dark-40)" lineClamp={1}>
                            Email newsletter
                          </Text>
                        </div>
                      </div>
                    </div>
                    <div className={Styles.buttonAndTextContainer}>
                      <div className={Styles.buttonContainer}>
                        <FileTrigger
                          accept={ACCEPTED_IMAGE_MIME_TYPES_NO_SVG}
                          allowsMultiple={true}
                          onSelect={handleImagesSelect}
                        >
                          <Button
                            variant="ghost-tertiary"
                            size={isMobile ? 'lg' : 'md'}
                            frontIcon={Upload5}
                            isDisabled={isCreatingMediaObjects}
                            fullWidth={isMobile}
                          >
                            Upload Images
                          </Button>
                        </FileTrigger>
                      </div>
                      <Text variant="metadata" color="var(--dark-40)">
                        JPG, PNG
                      </Text>
                    </div>
                  </div>
                )}
              </>
            )}
            {visuals.length > 0 && (
              <div className={Styles.visualsContainer}>
                <div>
                  {visuals.map((visual) => (
                    <Row
                      type="visual"
                      url={visual.source_url}
                      key={visual.id}
                      onDelete={() => handleDeleteVisual(visual.id)}
                    />
                  ))}
                </div>
                <div>
                  <FileTrigger
                    accept={ACCEPTED_IMAGE_MIME_TYPES_NO_SVG}
                    allowsMultiple={true}
                    onSelect={handleImagesSelect}
                  >
                    <Button
                      variant={isOnboardingRedesign ? 'secondary' : 'ghost'}
                      frontIcon={Plus}
                      isDisabled={isCreatingMediaObjects}
                    >
                      Add More
                    </Button>
                  </FileTrigger>
                </div>
              </div>
            )}
          </div>
          <div className={Styles.section}>
            <div className={Styles.brandDocsDescription}>
              {!isOnboardingRedesign && (
                <ResponsiveDesign.Desktop>
                  <Image size={36} />
                </ResponsiveDesign.Desktop>
              )}
              <div className={cx(Styles.textContainer, Styles.withPill)}>
                {isOnboardingRedesign ? (
                  <Heading level={4}>
                    Upload brand documents so we can create a brand voice
                    <span>
                      <Chip variant="flat" size="xs">
                        Optional
                      </Chip>
                    </span>
                  </Heading>
                ) : (
                  <Heading level={isMobile ? 5 : 4}>
                    Optional: Upload brand documents so we can create your Brand Profile
                  </Heading>
                )}
                <Text variant="secondary" color={isOnboardingRedesign ? 'var(--dark-40)' : 'var(--dark-80)'}>
                  Add files that describe your brand or business, such as presentations, profiles, or marketing
                  collateral.
                </Text>
              </div>
            </div>
            {documents.length === 0 && (
              <>
                {isOnboardingRedesign ? (
                  <div className={Styles.brandVisualsEmptyStateRedesigned}>
                    <div className={Styles.top}>
                      <img
                        src="https://res.cloudinary.com/almanac/image/upload/v1765809079/blaze_assets/virtual_marketer/onboarding/brand_documents_djwk9w.png"
                        alt="Documents"
                        className={Styles.brandVisualsEmptyStateImage}
                      />
                      <Text variant="secondary" color="var(--dark-40)">
                        Examples include: <br /> Business docs, presentations, marketing collateral
                      </Text>
                    </div>
                    <div className={Styles.bottom}>
                      <FileTrigger
                        accept={['application/pdf', '.txt', '.md']}
                        allowsMultiple={true}
                        onSelect={handleDocumentsSelect}
                      >
                        <Button
                          variant={isOnboardingRedesign ? 'secondary' : 'ghost-tertiary'}
                          size="xl"
                          frontIcon={isOnboardingRedesign ? Upload5 : Upload5}
                          isDisabled={isCreatingMediaObjects}
                          fullWidth={isMobile}
                        >
                          Upload Files
                        </Button>
                      </FileTrigger>
                      <Text variant="metadata" color="var(--dark-40)">
                        Supported formats: PDF, TXT, Markdown
                      </Text>
                    </div>
                  </div>
                ) : (
                  <div className={Styles.brandDocumentsEmptyState}>
                    <div className={Styles.examples}>
                      <Text variant="label">Examples:</Text>
                      <div className={Styles.exampleImagesContainer}>
                        <div className={Styles.exampleImageContainer}>
                          <img
                            src="https://res.cloudinary.com/almanac/image/upload/v1758557778/onboarding_assets/no_website_step/brand_doc_example_1_lanrt9.png"
                            alt="Business doc"
                          />
                          <Text variant="metadata" color="var(--dark-40)">
                            Business doc
                          </Text>
                        </div>
                        <div className={Styles.exampleImageContainer}>
                          <img
                            src="https://res.cloudinary.com/almanac/image/upload/v1758557835/onboarding_assets/no_website_step/brand_doc_example_2_ea6wq5.png"
                            alt="Presentations"
                          />
                          <Text variant="metadata" color="var(--dark-40)">
                            Presentations
                          </Text>
                        </div>
                        <div className={Styles.exampleImageContainer}>
                          <img
                            src="https://res.cloudinary.com/almanac/image/upload/v1758557871/onboarding_assets/no_website_step/brand_doc_example_3_assveb.png"
                            alt="Marketing collateral"
                          />
                          <Text variant="metadata" color="var(--dark-40)" lineClamp={1}>
                            {isMobile ? 'Collateral' : 'Marketing collateral'}
                          </Text>
                        </div>
                      </div>
                    </div>
                    <div className={Styles.buttonAndTextContainer}>
                      <div className={Styles.buttonContainer}>
                        <FileTrigger
                          accept={['application/pdf', '.txt', '.md']}
                          allowsMultiple={true}
                          onSelect={handleDocumentsSelect}
                        >
                          <Button
                            variant="ghost-tertiary"
                            size={isMobile ? 'lg' : 'md'}
                            frontIcon={Upload5}
                            isDisabled={isCreatingMediaObjects}
                            fullWidth={isMobile}
                          >
                            Upload Files
                          </Button>
                        </FileTrigger>
                      </div>
                      <Text variant="metadata" color="var(--dark-40)">
                        PDF, TXT, Markdown
                      </Text>
                    </div>
                  </div>
                )}
              </>
            )}
            {documents.length > 0 && (
              <div className={Styles.documentsContainer}>
                <div>
                  {documents.map((document) => (
                    <Row
                      type="document"
                      url={document.source_url}
                      key={document.id}
                      onDelete={() => handleDeleteDocument(document.id)}
                    />
                  ))}
                </div>
                <div>
                  <FileTrigger
                    accept={['application/pdf', '.txt', '.md']}
                    allowsMultiple={true}
                    onSelect={handleDocumentsSelect}
                  >
                    <Button
                      variant={isOnboardingRedesign ? 'secondary' : 'ghost'}
                      frontIcon={Plus}
                      isDisabled={isCreatingMediaObjects}
                    >
                      Add More
                    </Button>
                  </FileTrigger>
                </div>
              </div>
            )}
            <div className={Styles.additionalTextContainer}>
              {isOnboardingRedesign ? (
                <label htmlFor="additional-text">
                  <Heading level={4}>Or just add anything else here</Heading>
                </label>
              ) : (
                <label htmlFor="additional-text">
                  <Text variant="smallList">Or, just add anything else here</Text>
                </label>
              )}
              <Input
                className={cx(Styles.input, { [Styles.darkBackground]: isMobile && additionalText.length === 0 })}
                rows={2}
                isTextarea={isMobile}
                id="additional-text"
                name="additional-text"
                value={additionalText}
                onChange={(e) => setAdditionalText(e.target.value)}
                placeholder="Paste a tagline, mission statement, or brand description"
                size="lg"
                autoFocus={false}
                autoCorrect="off"
              />
            </div>
          </div>
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}
